import { Link } from "react-router-dom";
import { ShieldCheck, Eye, BadgeCheck } from "lucide-react";
import SEO from "../components/SEO";
import GetStartedBar from "../components/GetStartedBar";

export default function AboutPage() {
  return (
    <div className="bg-bg text-ink">
      <SEO
        title="About"
        description="Sift is a services marketplace built so providers keep more and businesses pay less. Find vetted providers, book services, and run the workforce back office in the same place."
        path="/about"
        keywords={["about sift careers", "services marketplace", "direct hire platform", "no markup staffing", "contractor management platform", "contingent workforce management", "staffing technology platform"]}
      />

      {/* Hero */}
      <section className="px-4 sm:px-6 pt-14 pb-10 sm:pt-20 sm:pb-14">
        <div className="max-w-5xl mx-auto text-center">
          <div className="font-mono uppercase text-[0.65625rem] tracking-[.24em] text-brand">Our story</div>
          <h1 className="mt-4 font-sans font-bold tracking-tight leading-[1.05] text-3xl sm:text-4xl lg:text-[3.25rem]">
            A marketplace built for the people who <span className="font-display italic font-semibold">do the work.</span>
          </h1>
          <p className="mt-5 text-base sm:text-lg text-secondary max-w-2xl mx-auto leading-relaxed">
            Sift is a services marketplace and a workforce platform in one. Businesses find vetted
            providers, book services, and pay directly. Companies that hire workers themselves run
            their full back office in the same place: contracts, credentials, timecards, payments.
            No agency markups, no placement fees, no hidden margins.
          </p>

          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/get-started"
              className="inline-flex items-center justify-center bg-brand hover:bg-brand-hover text-white px-7 py-3.5 rounded-pill font-semibold shadow-sift-md transition-colors"
            >
              For businesses
            </Link>
            <Link
              to="/get-started?a=talent"
              className="inline-flex items-center justify-center bg-surface border border-line-strong text-ink px-7 py-3.5 rounded-pill font-semibold hover:border-brand hover:text-brand transition-colors"
            >
              For talent
            </Link>
          </div>
        </div>
      </section>

      {/* Founder note */}
      <section className="px-4 sm:px-6 py-10">
        <div className="max-w-4xl mx-auto">
          <div className="rounded-2xl border border-line bg-surface shadow-sift-sm p-6 sm:p-9"
            style={{ background: "linear-gradient(135deg, var(--brand-tint), var(--surface) 62%)" }}>
            <div className="flex items-start gap-5">
              <span className="hidden sm:grid place-items-center shrink-0 rounded-full text-white font-bold"
                style={{ width: 60, height: 60, fontSize: "1.375rem", background: "linear-gradient(135deg,#15604A,#1B6B4F)" }}>
                JS
              </span>
              <div className="min-w-0">
                <div className="font-mono uppercase text-[0.625rem] tracking-[.2em] text-brand mb-2">A note from the founder</div>
                <h2 className="font-display italic text-2xl sm:text-3xl text-ink">Why I built Sift</h2>
                <div className="mt-4 space-y-4 text-[0.96875rem] leading-relaxed text-secondary">
                  <p>
                    In 2022 I got thrown into a staffing crisis running healthcare operations, managing
                    80+ contractors at a major hospital system. The work was the easy part. The chaos
                    around it wasn't: spreadsheets that broke every finance report, timecards stuck in
                    someone's inbox while a contractor went six weeks without pay, credentials that
                    expired the morning of a shift, onboarding scattered across five separate emails.
                  </p>
                  <p>
                    I'm an attorney by training, based in Carmel, Indiana. I built Sift on nights and
                    weekends, while working full-time and raising a toddler. No investors, no co-founder,
                    just a stubborn belief that the people doing the work should keep more of what they
                    earn, and that the businesses hiring them shouldn't drown in busywork to make it happen.
                  </p>
                  <p className="text-ink font-medium">
                    Sift exists to move value toward the people who create it, not away from them. That's
                    the whole point.
                  </p>
                </div>
                <div className="mt-5 text-sm">
                  <span className="font-semibold text-ink">Jake Sutton</span>
                  <span className="text-tertiary"> · Founder, Sift Careers</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Who we serve */}
      <section className="px-4 sm:px-6 py-10 sm:py-14">
        <div className="max-w-6xl mx-auto">
          <SectionHead eyebrow="Who it's for" title="Who we serve" />
          <div className="grid gap-4 sm:gap-5 md:grid-cols-2">
            <Card
              title="Businesses buying services"
              body="Need roofing, HVAC, cleaning, healthcare staffing, or a fractional CFO? Browse vetted providers, book directly, and pay through the platform. No agency in the middle taking a cut."
            />
            <Card
              title="Service providers"
              body="Roofers, trades, healthcare workers, fractional professionals. Get found, get booked, get paid direct. Sift takes one transparent fee on the payment, not 30-60% off your rate."
            />
            <Card
              title="Mid-market companies"
              body="Manufacturers, healthcare systems, and growing businesses that hire workers themselves. Track every contractor, employee, and fractional professional in one place."
            />
            <Card
              title="Staffing agencies"
              body="White-label the platform under your brand. Your clients see your technology, not a third-party vendor. Manage placements, timecards, margin visibility, and client portals from one system."
            />
          </div>
        </div>
      </section>

      {/* What we do */}
      <section className="px-4 sm:px-6 py-10 sm:py-14">
        <div className="max-w-6xl mx-auto">
          <SectionHead eyebrow="The platform" title="What we do" />
          <div className="grid gap-4 sm:gap-5 md:grid-cols-2">
            <Card title="Contractor lifecycle management" body="From onboarding to payment to conversion. Contracts, assignments, timecards, approvals, and direct deposit through Stripe. The full post-placement lifecycle in one workflow." />
            <Card title="Credential and compliance tracking" body="Licenses, certifications, insurance, background checks. Track expiration dates, verify status, and generate audit-ready packages. Know who's compliant before someone asks." />
            <Card title="Workforce analytics" body="Temp-to-hire conversion rates, credential compliance percentages, headcount by department, budget burn rates. The metrics your CFO and CPO actually need." />
            <Card title="Temp-to-hire conversion" body="When a contractor is ready to go permanent, initiate the conversion from the platform. The contractor gets an offer with salary, benefits, and start date. Accept, and they move from 1099 to W-2." />
            <Card title="White-label partner portals" body="Staffing agencies run their business on Sift under their own brand. Custom subdomain, logo, colors. Their clients never see Sift. They see your technology." />
            <Card title="Financial visibility" body="P&L statements, purchase order tracking, budget projections, and payment history. Know what you're spending on contingent labor across every department." />
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="px-4 sm:px-6 py-10 sm:py-14">
        <div className="max-w-6xl mx-auto">
          <SectionHead eyebrow="The Sift Method" title="How it works" />
          <div className="grid gap-4 sm:gap-5 md:grid-cols-2 lg:grid-cols-4">
            <StepCard step="1" title="Set up your org" body="Create your organization, add departments, invite your team. Takes about 5 minutes." />
            <StepCard step="2" title="Bring on contractors" body="Create contracts, assign workers, request credentials. Contractors self-onboard from their phone." />
            <StepCard step="3" title="Run the work" body="Contractors submit timecards. Managers approve. Payments process automatically through Stripe." />
            <StepCard step="4" title="Convert or complete" body="When a contractor is ready for permanent hire, send an offer. When a job is done, close the contract. Full history preserved." />
          </div>
        </div>
      </section>

      {/* Principles */}
      <section className="px-4 sm:px-6 py-10 sm:py-14">
        <div className="max-w-6xl mx-auto">
          <SectionHead eyebrow="Our principles" title="What we believe" />
          <div className="grid gap-4 sm:gap-5 md:grid-cols-3">
            <Principle Icon={ShieldCheck} title="Transparency over markups" body="Contractors keep 100% of their rate. Companies pay a flat platform fee. No hidden margins, no conversion fees, no surprises." />
            <Principle Icon={Eye} title="Same truth for both sides" body="Companies and contractors see the same data. Shared timecards, mirrored approvals, readable invoices. Trust is built on visibility." />
            <Principle Icon={BadgeCheck} title="Compliance is not optional" body="Every credential tracked, every expiration flagged, every audit trail preserved. When the auditor shows up, you're ready." />
          </div>
        </div>
      </section>

      {/* Closing CTA band */}
      <section className="px-4 sm:px-6 pt-4 pb-16">
        <div className="max-w-6xl mx-auto">
          <div className="relative overflow-hidden rounded-2xl px-6 sm:px-10 py-12 text-center" style={{ background: "var(--ink)", color: "var(--on-dark)" }}>
            <div aria-hidden="true" style={{ position: "absolute", top: "-40%", left: "50%", transform: "translateX(-50%)", width: 520, height: 520, background: "radial-gradient(50% 50% at 50% 50%, rgba(224,201,136,.18), rgba(224,201,136,0) 70%)", pointerEvents: "none" }} />
            <div className="relative">
              <h3 className="font-sans font-bold tracking-tight text-2xl sm:text-3xl">Ready to see it?</h3>
              <p className="mt-3 text-[0.96875rem]" style={{ color: "var(--on-dark-muted)" }}>Start with your first contract. No subscription required.</p>
              <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
                <Link to="/get-started" className="inline-flex items-center justify-center bg-brand hover:bg-brand-hover text-white px-7 py-3.5 rounded-pill font-semibold transition-colors">
                  For businesses
                </Link>
                <Link to="/get-started?a=talent" className="inline-flex items-center justify-center px-7 py-3.5 rounded-pill font-semibold transition-colors" style={{ background: "rgba(255,255,255,.1)", color: "#fff", border: "1px solid rgba(255,255,255,.2)" }}>
                  For talent
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      <GetStartedBar message="See your dashboard before you sign up." sub="Free to join. Set up in under two minutes." location="about_sticky_bar" />
    </div>
  );
}

function SectionHead({ eyebrow, title }) {
  return (
    <div className="mb-6">
      <div className="font-mono uppercase text-[0.625rem] tracking-[.24em] text-brand">{eyebrow}</div>
      <h2 className="mt-2 font-display italic text-2xl sm:text-3xl text-ink">{title}</h2>
    </div>
  );
}

function Card({ title, body }) {
  return (
    <div className="bg-surface border border-line rounded-2xl p-5 sm:p-6 shadow-sift-sm">
      <div className="font-semibold text-ink mb-2 text-base sm:text-lg">{title}</div>
      <p className="text-secondary text-sm sm:text-[0.9375rem] leading-relaxed">{body}</p>
    </div>
  );
}

function StepCard({ step, title, body }) {
  return (
    <div className="bg-surface border border-line rounded-2xl p-5 sm:p-6 shadow-sift-sm">
      <span className="grid place-items-center rounded-full bg-brand text-white font-bold text-sm mb-3" style={{ width: 30, height: 30 }}>{step}</span>
      <div className="font-semibold text-ink text-base sm:text-lg mb-1">{title}</div>
      <p className="text-secondary text-sm sm:text-[0.9375rem] leading-relaxed mt-1">{body}</p>
    </div>
  );
}

function Principle({ Icon, title, body }) {
  return (
    <div className="bg-surface border border-line rounded-2xl p-5 sm:p-6 shadow-sift-sm">
      <span className="grid place-items-center rounded-xl bg-brand-tint mb-3" style={{ width: 40, height: 40 }}>
        <Icon className="h-5 w-5 text-brand" strokeWidth={1.8} aria-hidden="true" />
      </span>
      <div className="font-semibold text-ink mb-2 text-base sm:text-lg">{title}</div>
      <p className="text-secondary text-sm sm:text-[0.9375rem] leading-relaxed">{body}</p>
    </div>
  );
}
